import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, Eye, Users, Clock, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const FILTERS = [
  { value: 'all', label: 'Tous les inscrits' },
  { value: 'verified', label: 'Entreprises verifiees' },
  { value: 'has_listings', label: 'Avec annonces actives' },
  { value: 'no_listings', label: 'Sans annonces' },
  { value: 'sector:piscines-wellness', label: 'Secteur: Piscines & Wellness' },
  { value: 'sector:construction', label: 'Secteur: Construction' },
  { value: 'sector:hvac-energie', label: 'Secteur: HVAC & Energie' },
  { value: 'sector:amenagement-exterieur', label: 'Secteur: Amenagement Exterieur' },
  { value: 'sector:outillage-pro', label: 'Secteur: Outillage Pro' },
  { value: 'sector:materiaux-finitions', label: 'Secteur: Materiaux & Finitions' },
];

export function NewsletterTab() {
  const [tab, setTab] = useState('compose'); // compose | history
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [filter, setFilter] = useState('all');
  const [recipientCount, setRecipientCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  // History
  const [campaigns, setCampaigns] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);

  // Load recipient count when filter changes
  useEffect(() => {
    setLoadingCount(true);
    setRecipientCount(null);
    api.get(`/admin/newsletters/recipients?filter=${encodeURIComponent(filter)}`)
      .then(({ data }) => setRecipientCount(data.count))
      .catch(() => setRecipientCount(0))
      .finally(() => setLoadingCount(false));
  }, [filter]);

  // Load history
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/newsletters?page=${historyPage}`);
      setCampaigns(data.newsletters);
      setHistoryTotal(data.total);
      setHistoryPages(data.pages);
    } catch (err) { console.error('Failed to load newsletters:', err); }
  }, [historyPage]);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const handlePreview = async () => {
    if (!subject || !content) { toast.error('Remplissez le sujet et le contenu'); return; }
    try {
      const { data } = await api.post('/admin/newsletters/preview', { subject, content, cta_text: ctaText || null, cta_url: ctaUrl || null });
      setPreview(data.html);
    } catch (err) { toast.error('Erreur de preview'); }
  };

  const handleSend = async () => {
    if (!subject || !content) { toast.error('Remplissez le sujet et le contenu'); return; }
    setSending(true);
    try {
      const { data } = await api.post('/admin/newsletters/send', {
        subject, content,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null,
        recipient_filter: filter,
      });
      toast.success(`${data.message}`);
      setConfirmSend(false);
      setSubject('');
      setContent('');
      setCtaText('');
      setCtaUrl('');
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-tab">
      {/* Tab switch */}
      <div className="flex gap-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setTab('compose')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'compose' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
          data-testid="newsletter-compose-tab"
        >
          Composer
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'history' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
          data-testid="newsletter-history-tab"
        >
          Historique ({historyTotal})
        </button>
      </div>

      {tab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Compose form */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">Objet de l'email</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Ex: Nouveau service sur DestockPro"
                className="w-full border dark:border-neutral-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-[#F25C05]/30 focus:border-[#F25C05] outline-none"
                data-testid="newsletter-subject"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">Contenu du message</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Ecrivez votre message ici...&#10;&#10;Les retours a la ligne sont preserves.&#10;Vous pouvez ecrire en texte simple."
                rows={12}
                className="w-full border dark:border-neutral-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-[#F25C05]/30 focus:border-[#F25C05] outline-none resize-y"
                data-testid="newsletter-content"
              />
              <p className="text-[10px] text-neutral-400 mt-1">Les retours a la ligne sont automatiquement convertis. Chaque email commence par "Bonjour Prenom,"</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">Texte du bouton (optionnel)</label>
                <input
                  type="text"
                  value={ctaText}
                  onChange={e => setCtaText(e.target.value)}
                  placeholder="Ex: Voir les annonces"
                  className="w-full border dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white outline-none"
                  data-testid="newsletter-cta-text"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">Lien du bouton (optionnel)</label>
                <input
                  type="text"
                  value={ctaUrl}
                  onChange={e => setCtaUrl(e.target.value)}
                  placeholder="https://www.destockpro.be/..."
                  className="w-full border dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white outline-none"
                  data-testid="newsletter-cta-url"
                />
              </div>
            </div>

            {/* Recipient filter */}
            <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 border dark:border-neutral-700">
              <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                <Users className="w-3.5 h-3.5 inline mr-1" /> Destinataires
              </label>
              <select
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="w-full border dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white mb-2"
                data-testid="newsletter-filter"
              >
                {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-bold ${recipientCount === 0 ? 'text-red-500' : 'text-[#1F3F72] dark:text-blue-400'}`}>
                  {loadingCount ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `${recipientCount ?? '—'} destinataire(s)`}
                </div>
                <span className="text-[10px] text-neutral-400">(admins et desinscrits exclus)</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePreview} disabled={!subject || !content} data-testid="newsletter-preview-btn">
                <Eye className="w-4 h-4 mr-1.5" /> Previsualiser
              </Button>
              {!confirmSend ? (
                <Button
                  onClick={() => setConfirmSend(true)}
                  disabled={!subject || !content || recipientCount === 0}
                  className="bg-[#F25C05] hover:bg-[#d94f04] text-white"
                  data-testid="newsletter-send-btn"
                >
                  <Send className="w-4 h-4 mr-1.5" /> Envoyer
                </Button>
              ) : (
                <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-3 py-1.5">
                  <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                  <span className="text-xs text-orange-700 dark:text-orange-300">
                    Envoyer a {recipientCount} personne(s) ?
                  </span>
                  <Button size="sm" onClick={handleSend} disabled={sending} className="bg-[#F25C05] hover:bg-[#d94f04] text-white text-xs" data-testid="newsletter-confirm-send">
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirmer'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmSend(false)} className="text-xs">Annuler</Button>
                </div>
              )}
            </div>
          </div>

          {/* Right: Preview */}
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5">Apercu</label>
            <div className="border dark:border-neutral-700 rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-900 min-h-[400px]">
              {preview ? (
                <iframe
                  srcDoc={preview}
                  title="Preview"
                  className="w-full h-[500px] border-0"
                  sandbox=""
                  data-testid="newsletter-preview-frame"
                />
              ) : (
                <div className="flex items-center justify-center h-[400px] text-neutral-400 text-sm">
                  <div className="text-center">
                    <Eye className="w-10 h-10 mx-auto mb-2 opacity-20" />
                    <p>Cliquez sur "Previsualiser" pour voir le rendu</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4" data-testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      c.status === 'sent' ? 'text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-400' :
                      c.status === 'sending' ? 'text-orange-600 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400' :
                      'text-neutral-500 bg-neutral-100'
                    }`}>
                      {c.status === 'sent' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {c.status === 'sent' ? 'Envoye' : c.status === 'sending' ? 'En cours...' : c.status}
                    </span>
                    <span className="text-[10px] text-neutral-400">
                      {new Date(c.sent_at).toLocaleDateString('fr-BE', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">{c.subject}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">{c.content}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-[#1F3F72] dark:text-blue-400">{c.sent_count}</p>
                  <p className="text-[10px] text-neutral-400">envoye(s)</p>
                  {c.failed_count > 0 && <p className="text-[10px] text-red-400">{c.failed_count} echec(s)</p>}
                </div>
              </div>
              <div className="flex gap-3 mt-2 pt-2 border-t dark:border-neutral-700">
                <span className="text-[10px] text-neutral-400">Filtre: {FILTERS.find(f => f.value === c.recipient_filter)?.label || c.recipient_filter}</span>
                <span className="text-[10px] text-neutral-400">Par: {c.sent_by}</span>
              </div>
            </div>
          ))}

          {campaigns.length === 0 && (
            <div className="text-center py-12 text-neutral-400">
              <Send className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Aucune campagne envoyee</p>
            </div>
          )}

          {historyPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" disabled={historyPage <= 1} onClick={() => setHistoryPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-neutral-500">{historyPage}/{historyPages}</span>
              <Button size="sm" variant="outline" disabled={historyPage >= historyPages} onClick={() => setHistoryPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
